import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@fluentui/react-components';
import { storeService } from '@services/storeService';
import JoinAlyzePlus from '@/assets/JoinAlyzePlus.png';
import AlyzeWhiteLogo from '@/assets/AlyzeWhiteLogo.svg';

const useStyles = makeStyles({
    footer: {
        padding: '80px',
        backgroundColor: '#141414',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '64px',
        '@media (max-width: 1024px)': {
            padding: '40px 24px',
            gap: '48px',
        },
        '@media (max-width: 768px)': {
            padding: '32px 16px',
            gap: '32px',
        },
    },

    mainContent: {
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '64px',
        '@media (max-width: 1024px)': {
            flexDirection: 'column',
            gap: '48px',
        },
        '@media (max-width: 768px)': {
            gap: '32px',
        },
    },

    heroImage: {
        width: '454px',
        height: '546px',
        borderRadius: '16px',
        objectFit: 'cover',
        flexShrink: 0,
        '@media (max-width: 1024px)': {
            display: 'none', // Hide on tablet and mobile like the original
        },
    },

    contentWrapper: {
        flex: '1 1 0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '64px',
        '@media (max-width: 768px)': {
            gap: '48px',
        },
    },

    linksSection: {
        alignSelf: 'stretch',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '64px',
        '@media (max-width: 968px)': {
            gridTemplateColumns: '1fr 1fr',
            gap: '32px',
        },
        '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr',
            gap: '32px',
        },
    },

    linkColumn: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '24px',
    },

    companyColumn: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '32px',
        '@media (max-width: 968px)': {
            gridColumn: '1 / -1',
        },
    },

    columnTitle: {
        alignSelf: 'stretch',
        color: '#999999',
        fontSize: '14px',
        fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '2.24px',
        lineHeight: '20px',
        marginBottom: '8px',
    },

    linkList: {
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '8px',
    },

    linkItem: {
        alignSelf: 'stretch',
        color: '#E6E6E6',
        fontSize: '16px',
        fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 600,
        lineHeight: '22px',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.2s ease',
        '&:hover': {
            color: '#C9A774',
        },
    },

    companyInfo: {
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '15px',
    },

    contactInfo: {
        alignSelf: 'stretch',
        color: '#E6E6E6',
        fontSize: '16px',
        fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 400,
        lineHeight: '22px',
        margin: 0,
    },

    separator: {
        alignSelf: 'stretch',
        height: '1px',
        backgroundColor: '#616161',
        margin: '16px 0',
    },

    socialIcons: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '24px',
        alignItems: 'center',
    },

    socialIcon: {
        width: '24px',
        height: '24px',
        color: '#C9A774',
        cursor: 'pointer',
        transition: 'color 0.2s ease',
        '&:hover': {
            color: '#B8966A',
        },
    },

    socialSvg: {
        width: '100%',
        height: '100%',
        fill: 'currentColor',
    },

    bottomSection: {
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '32px',
    },

    bottomHeader: {
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
        },
    },

    logoContainer: {
        display: 'flex',
        alignItems: 'center',
    },

    logo: {
        height: '25px',
        width: 'auto',
    },

    legalLinks: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '48px',
        '@media (max-width: 768px)': {
            gap: '24px',
            alignSelf: 'stretch',
        },
    },

    legalLink: {
        textAlign: 'center',
        color: '#E6E6E6',
        fontSize: '14px',
        fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 600,
        lineHeight: '20px',
        textDecoration: 'none',
        transition: 'color 0.2s ease',
        '&:hover': {
            color: '#C9A774',
        },
    },

    copyrightSection: {
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    copyright: {
        alignSelf: 'stretch',
        color: '#E6E6E6',
        fontSize: '14px',
        fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 600,
        lineHeight: '20px',
        margin: 0,
    },

    disclaimers: {
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '16px',
        '@media (max-width: 1024px)': {
            flexDirection: 'column',
        },
    },

    disclaimer: {
        flex: '1 1 0',
        color: '#999999',
        fontSize: '14px',
        fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        lineHeight: '20px',
    },

    disclaimerTitle: {
        fontWeight: 700,
        display: 'block',
        marginBottom: '4px',
    },

    disclaimerText: {
        fontWeight: 400,
    },
});

export const StoreFooterV1: React.FC = () => {
    const styles = useStyles();
    const [categories, setCategories] = useState<string[]>([]);
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoryList = await storeService.getProductCategories();
                setCategories(categoryList);
            } catch (error) {
                console.error('Error fetching categories:', error);
                // Fallback to hardcoded categories if API fails
                setCategories([
                    'Metabolism & Weight Loss',
                    'Energy & Performance',
                    'Hormonal & Sexual Health',
                    'Everyday Health',
                    'Anti-Aging & Vitality',
                    'Digestive & Gut Health',
                    'Inflammation & Immunity',
                    'Mind & Focus',
                    'Sleep & Calm',
                    'Home Detox'
                ]);
            }
        };

        fetchCategories();
    }, []);

    const formatCategoryForUrl = (categoryName: string) => {
        // First decode HTML entities, then format for URL slug
        const tempElement = document.createElement('div');
        tempElement.innerHTML = categoryName;
        const decodedName = tempElement.textContent || tempElement.innerText || categoryName;
        // Convert to lowercase, replace spaces and & with dashes, remove special characters
        return decodedName.toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/&/g, '')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/--+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const decodeHtmlEntities = (text: string) => {
        const tempElement = document.createElement('div');
        tempElement.innerHTML = text;
        return tempElement.textContent || tempElement.innerText || text;
    };

    return (
        <footer className={styles.footer}>
            <div className={styles.mainContent}>
                <Link to="/v1/memberships" className={styles.heroImage}>
                    <img
                        src={JoinAlyzePlus}
                        alt="Join ALYZE+ - Know yourself like never before"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }}
                    />
                </Link>

                <div className={styles.contentWrapper}>
                    <div className={styles.linksSection}>
                        <div className={styles.linkColumn}>
                            <h2 className={styles.columnTitle}>Learn</h2>
                            <div className={styles.linkList}>
                                {categories.map((category) => (
                                    <Link
                                        key={category}
                                        to={`/v1/product-category/${formatCategoryForUrl(category)}`}
                                        className={styles.linkItem}
                                    >
                                        {decodeHtmlEntities(category)}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className={styles.linkColumn}>
                            <h2 className={styles.columnTitle}>Popular</h2>
                            <div className={styles.linkList}>
                                <a href="https://alyze.health/shop/product/countertop-water-machine" className={styles.linkItem}>
                                    Water Machine
                                </a>
                                <a href="https://alyze.health/shop/product/ultralux-water-tablets/" className={styles.linkItem}>
                                    Water Tablets
                                </a>
                                <a href="https://alyze.health/shop/product/red-max/" className={styles.linkItem}>
                                    Red Max
                                </a>
                                <a href="https://alyze.health/shop/product/air-sentry-500/" className={styles.linkItem}>
                                    Air Sentry 500
                                </a>
                                <a href="https://alyze.health/shop/product/ultralux-foldaway-sauna/" className={styles.linkItem}>
                                    Foldaway Sauna
                                </a>
                                <a href="https://alyze.health/shop/product/ultralux-health-hardshell-i-90-hbot-by-oxyhelp/" className={styles.linkItem}>
                                    Hyperbaric Chamber
                                </a>
                                <a href="https://alyze.health/shop/product/at-night-fat-burner-supplement/" className={styles.linkItem}>
                                    At Night Fat Burn
                                </a>
                                <a href="https://alyze.health/shop/product/adhd-memory-support-supplement/" className={styles.linkItem}>
                                    ADHD+ Memory Support
                                </a>
                            </div>
                        </div>

                        <div className={styles.companyColumn}>
                            <div className={styles.companyInfo}>
                                <h2 className={styles.columnTitle}>Company</h2>
                                <p className={styles.contactInfo}>
                                    support@alyze.health<br />
                                    M-F 8:00am  -  4:00pm MST
                                </p>
                                <p className={styles.contactInfo}>
                                    782 S Auto Mall Dr. Suite C<br />
                                    American Fork, UT 84003
                                </p>
                            </div>
                            <div className={styles.separator}></div>
                            <div className={styles.socialIcons}>
                                <a href="https://www.instagram.com/alyzehealth/" className={styles.socialIcon} aria-label="Instagram">
                                    <svg className={styles.socialSvg} viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
                                    </svg>
                                </a>
                                <a href="https://www.facebook.com/people/ALYZE-Health/61583241363324/" className={styles.socialIcon} aria-label="Facebook">
                                    <svg className={styles.socialSvg} viewBox="0 0 320 512" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" />
                                    </svg>
                                </a>
                                <a href="https://www.tiktok.com/@alyze.health" className={styles.socialIcon} aria-label="TikTok">
                                    <svg className={styles.socialSvg} viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z" />
                                    </svg>
                                </a>
                                <a href="https://www.youtube.com/@alyze.health" className={styles.socialIcon} aria-label="YouTube">
                                    <svg className={styles.socialSvg} viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className={styles.bottomSection}>
                        <div className={styles.bottomHeader}>
                            <div className={styles.logoContainer}>
                                <a href="https://alyze.health">
                                    <img
                                        src={AlyzeWhiteLogo}
                                        alt="ALYZE Health"
                                        className={styles.logo}
                                    />
                                </a>
                            </div>
                            <div className={styles.legalLinks}>
                                <Link to="/legal/terms-of-service" className={styles.legalLink}>
                                    Terms of service
                                </Link>
                                <Link to="/legal/privacy-policy" className={styles.legalLink}>
                                    Privacy policy
                                </Link>
                            </div>
                        </div>

                        <div className={styles.copyrightSection}>
                            <p className={styles.copyright}>
                                Copyright© {currentYear} All in One Partners, LLC d/b/a ALYZE - All Rights Reserved.
                            </p>

                            <div className={styles.disclaimers}>
                                <div className={styles.disclaimer}>
                                    <span className={styles.disclaimerTitle}>FDA Disclaimer</span>
                                    <span className={styles.disclaimerText}>
                                        Statements made on this website have not been evaluated by the Food and Drug Administration.
                                        Products and services offered by All in One Partners, LLC d/b/a ALYZE are not intended to
                                        diagnose, treat, cure, or prevent any disease.
                                    </span>
                                </div>
                                <div className={styles.disclaimer}>
                                    <span className={styles.disclaimerTitle}>Medical Disclaimer</span>
                                    <span className={styles.disclaimerText}>
                                        The information provided by All in One Partners, LLC d/b/a ALYZE is for educational and
                                        general wellness purposes only. It is <strong>not intended as medical advice</strong>, and should not be
                                        used as a substitute for professional medical evaluation, diagnosis, or treatment. Always
                                        seek the advice of your physician or other qualified healthcare provider with any questions
                                        you may have regarding a medical condition or before starting any new health program,
                                        therapy, or supplement.
                                    </span>
                                </div>
                                <div className={styles.disclaimer}>
                                    <span className={styles.disclaimerTitle}>Liability Disclaimer</span>
                                    <span className={styles.disclaimerText}>
                                        By using All in One Partners, LLC d/b/a ALYZE products or services, you acknowledge and
                                        agree that you do so voluntarily and at your own risk. To the fullest extent permitted
                                        by law, All in One Partners, LLC d/b/a ALYZE disclaims any liability for injury, loss,
                                        or damages resulting from the use or misuse of its products or reliance on the information
                                        provided. Individual results may vary.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};
